import { AlertCircle, Calendar, ChevronRight, Search, TrendingUp, Users, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { bucketSubscriptionStatus } from '../lib/utils';
import { useAuthStore } from '../store/auth';

interface Agency {
  id: string;
  name: string;
  email: string;
  owner_name: string;
  subscription_status: string;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
  customer_count: number;
  vehicle_count: number;
  agent_count: number;
  total_events: number;
  created_at: string;
}

interface DashboardStats {
  total_agencies: number;
  active_subscription: number;
  trial_agencies: number;
  expired_subscriptions: number;
  total_customers: number;
  total_vehicles: number;
  agencies_expiring_soon: number;
}

export default function SuperAdminDashboard() {
  const token = useAuthStore((s) => s.accessToken);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [extensionType, setExtensionType] = useState<'trial' | 'subscription'>('subscription');
  const [extensionMonths, setExtensionMonths] = useState(1);
  const [extendingId, setExtendingId] = useState<string | null>(null);

  const limit = 10;

  useEffect(() => {
    fetchAgenciesAndStats();
  }, [page, search, token]);

  const fetchAgenciesAndStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) {
        params.append('search', search);
      }

      // Fetch current page data for table display
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/agencies?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch agencies');

      const data = await response.json();
      setAgencies(data.data || []);
      setTotal(data.total);
      setTotalPages(data.totalPages);

      // Fetch all agencies data for stats (no pagination)
      const statsParams = new URLSearchParams({
        page: '1',
        limit: '1000', // Large limit to get all agencies for stats
      });
      if (search) {
        statsParams.append('search', search);
      }

      const statsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/agencies?${statsParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!statsResponse.ok) throw new Error('Failed to fetch stats');
      const statsData = await statsResponse.json();
      const allAgencies = statsData.data || [];

      // Calculate stats from all agencies
      const stats: DashboardStats = {
        total_agencies: statsData.total || 0,
        active_subscription: allAgencies.filter(
          (a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'active'
        ).length,
        trial_agencies: allAgencies.filter(
          (a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'trial'
        ).length,
        expired_subscriptions: allAgencies.filter(
          (a: Agency) => bucketSubscriptionStatus(a.subscription_status) === 'expired'
        ).length,
        total_customers: allAgencies.reduce(
          (sum: number, a: Agency) => sum + a.customer_count,
          0
        ),
        total_vehicles: allAgencies.reduce(
          (sum: number, a: Agency) => sum + a.vehicle_count,
          0
        ),
        agencies_expiring_soon: allAgencies.filter((a: Agency) => {
          if (!a.subscription_ends_at && !a.trial_ends_at) return false;
          const expiryDate = new Date(a.subscription_ends_at || a.trial_ends_at || '');
          const daysUntilExpiry =
            (expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
          return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
        }).length,
      };
      setStats(stats);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleExtendSubscription = async () => {
    if (!selectedAgency) return;

    try {
      setExtendingId(selectedAgency.id);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/agencies/extend-subscription`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agency_id: selectedAgency.id,
            type: extensionType,
            months: extensionMonths,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to extend subscription');
      }

      toast.success(`Subscription extended by ${extensionMonths} month(s)`);
      setShowExtendModal(false);
      setSelectedAgency(null);
      setExtensionMonths(1);
      setExtensionType('subscription');
      
      // Refresh agencies data
      await fetchAgenciesAndStats();
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to extend subscription');
    } finally {
      setExtendingId(null);
    }
  };

  const openExtendModal = (agency: Agency) => {
    setSelectedAgency(agency);
    setShowExtendModal(true);
  };

  const closeExtendModal = () => {
    setShowExtendModal(false);
    setSelectedAgency(null);
    setExtensionMonths(1);
    setExtensionType('subscription');
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClass =
      'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold';
    switch (bucketSubscriptionStatus(status)) {
      case 'active':
        return `${baseClass} bg-green-100 text-green-800`;
      case 'trial':
        return `${baseClass} bg-blue-100 text-blue-800`;
      case 'expired':
        return `${baseClass} bg-red-100 text-red-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const getDaysUntilExpiry = (expiryDate: string | null) => {
    if (!expiryDate) return null;
    const daysRemaining = Math.ceil(
      (new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysRemaining;
  };

  return (
    <SuperAdminLayout>
      <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Monitor all agencies, subscriptions, and customer metrics
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Agencies */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Agencies</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats?.total_agencies || 0}
                </p>
              </div>
              <Users className="w-12 h-12 text-blue-500 opacity-20" />
            </div>
          </div>

          {/* Active Subscriptions */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Subscriptions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats?.active_subscription || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats && stats.total_agencies > 0
                    ? Math.round(((stats.active_subscription / stats.total_agencies) * 100))
                    : 0}
                  %
                </p>
              </div>
              <Zap className="w-12 h-12 text-green-500 opacity-20" />
            </div>
          </div>

          {/* Trial Agencies */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Trial Agencies</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats?.trial_agencies || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats && stats.total_agencies > 0
                    ? Math.round(((stats.trial_agencies / stats.total_agencies) * 100))
                    : 0}
                  %
                </p>
              </div>
              <Calendar className="w-12 h-12 text-blue-400 opacity-20" />
            </div>
          </div>

          {/* Expiring Soon */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Expiring Soon</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats?.agencies_expiring_soon || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1">Within 30 days</p>
              </div>
              <AlertCircle className="w-12 h-12 text-orange-500 opacity-20" />
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Customers */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-600 text-sm font-medium">Total Customers</p>
            <p className="text-4xl font-bold text-gray-900 mt-4">
              {stats?.total_customers || 0}
            </p>
            <p className="text-xs text-gray-500 mt-2">Across all agencies</p>
          </div>

          {/* Total Vehicles */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-600 text-sm font-medium">Total Vehicles</p>
            <p className="text-4xl font-bold text-gray-900 mt-4">
              {stats?.total_vehicles || 0}
            </p>
            <p className="text-xs text-gray-500 mt-2">Under management</p>
          </div>

          {/* Expired Subscriptions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-600 text-sm font-medium">Expired Subscriptions</p>
            <p className="text-4xl font-bold text-red-600 mt-4">
              {stats?.expired_subscriptions || 0}
            </p>
            <p className="text-xs text-gray-500 mt-2">Require renewal</p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link
            to="/superadmin/analytics"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
                <p className="text-sm text-gray-600 mt-1">
                  View detailed event tracking and user behavior
                </p>
              </div>
              <TrendingUp className="w-6 h-6 text-gray-400" />
            </div>
          </Link>

          <Link
            to="/superadmin/subscriptions"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Subscriptions</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Track subscription periods and manage renewals
                </p>
              </div>
              <Calendar className="w-6 h-6 text-gray-400" />
            </div>
          </Link>
        </div>

        {/* Agencies Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Recent Agencies</h2>
            <div className="flex-1 ml-4 max-w-xs">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search agencies..."
                  value={search}
                  onChange={handleSearch}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Agency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Customers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Vehicles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Days Left
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="mt-2">Loading agencies...</p>
                    </td>
                  </tr>
                ) : agencies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No agencies found
                    </td>
                  </tr>
                ) : (
                  agencies.map((agency) => (
                    <tr key={agency.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{agency.name}</p>
                          <p className="text-xs text-gray-600">{agency.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(agency.subscription_status)}>
                          {agency.subscription_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agency.customer_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agency.vehicle_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agency.subscription_ends_at
                          ? formatDate(agency.subscription_ends_at)
                          : agency.trial_ends_at
                            ? formatDate(agency.trial_ends_at)
                            : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const daysLeft = getDaysUntilExpiry(
                            agency.subscription_ends_at || agency.trial_ends_at
                          );
                          if (daysLeft === null) return <span className="text-sm text-gray-600">N/A</span>;
                          return (
                            <span
                              className={`text-sm font-medium ${
                                daysLeft <= 7
                                  ? 'text-red-600'
                                  : daysLeft <= 30
                                    ? 'text-orange-600'
                                    : 'text-green-600'
                              }`}
                            >
                              {daysLeft} days
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openExtendModal(agency)}
                            disabled={extendingId === agency.id}
                            className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {extendingId === agency.id ? (
                              <>
                                <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
                                Extending...
                              </>
                            ) : (
                              <>
                                Extend <Zap className="h-4 w-4" />
                              </>
                            )}
                          </button>
                          <Link
                            to={`/superadmin/agencies/${agency.id}`}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
                          >
                            View <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {agencies.length} of {total} agencies
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(Math.max(1, page - 1))}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Extend Subscription Modal */}
        {showExtendModal && selectedAgency && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={closeExtendModal}
              ></div>

              {/* Modal container */}
              <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all mx-auto mt-4 max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">Extend Subscription</h3>
                  <button
                    onClick={closeExtendModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Agency
                    </label>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">{selectedAgency.name}</p>
                      <p className="text-xs text-gray-600">{selectedAgency.email}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extension Type
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="extensionType"
                          value="trial"
                          checked={extensionType === 'trial'}
                          onChange={(e) => setExtensionType(e.target.value as 'trial' | 'subscription')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Trial Period</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="extensionType"
                          value="subscription"
                          checked={extensionType === 'subscription'}
                          onChange={(e) => setExtensionType(e.target.value as 'trial' | 'subscription')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Subscription Period</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Months to Extend
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="36"
                      value={extensionMonths}
                      onChange={(e) => setExtensionMonths(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2 px-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <p className="text-xs text-gray-600 mt-1">Maximum 36 months (3 years)</p>
                  </div>

                  {selectedAgency.subscription_ends_at || selectedAgency.trial_ends_at ? (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <p className="text-xs text-blue-900">
                        <strong>Current expiry:</strong> {' '}
                        {formatDate(
                          selectedAgency.subscription_ends_at || selectedAgency.trial_ends_at
                        )}
                      </p>
                    </div>
                  ) : null}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
                  <button
                    onClick={closeExtendModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExtendSubscription}
                    disabled={extendingId !== null}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {extendingId ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Extending...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Extend Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}
